// src/index.ts
import { ref } from "vue";

// src/context.ts
import { inject, getCurrentInstance } from "vue";

// src/util/objectPath.ts
function getObjectPath(obj, path) {
  if (path === "") {
    return obj;
  }
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    if (result === void 0 || result === null) {
      return void 0;
    }
    result = result[key];
  }
  return result;
}

// src/util/formatDate.ts
var DEFAULT_DATE_OPTIONS = {
  year: "numeric",
  month: "long",
  day: "numeric"
};
function formatDate(date, locale, options) {
  try {
    const dateOptions = options || DEFAULT_DATE_OPTIONS;
    return new Intl.DateTimeFormat(locale, dateOptions).format(date);
  } catch (error) {
    return null;
  }
}

// src/util/formatNumber.ts
var DEFAULT_NUMBER_OPTIONS = {
  style: "decimal"
};
function formatNumber(num, locale, options) {
  try {
    const numberOptions = options || DEFAULT_NUMBER_OPTIONS;
    return new Intl.NumberFormat(locale, numberOptions).format(num);
  } catch (error) {
    return null;
  }
}

// src/util/pluralForms.ts
function determinePluralKey(count, availableKeys) {
  const hasZero = availableKeys.includes("zero");
  const hasOne = availableKeys.includes("one");
  const hasOther = availableKeys.includes("other");
  if (count === 0 && hasZero) {
    return "zero";
  }
  if (count === 1 && hasOne) {
    return "one";
  }
  if (hasOther) {
    return "other";
  }
  return null;
}
function getFallbackPluralForm(value) {
  if (value.other !== void 0)
    return value.other;
  if (value.one !== void 0)
    return value.one;
  if (value.zero !== void 0)
    return value.zero;
  const firstKey = Object.keys(value)[0];
  if (firstKey) {
    return value[firstKey];
  }
  return "";
}
function getPluralForm(value, count) {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object" && value !== null) {
    const availableKeys = Object.keys(value);
    if (availableKeys.length === 0) {
      return "";
    }
    const pluralKey = determinePluralKey(count, availableKeys);
    if (pluralKey && value[pluralKey] !== void 0) {
      return value[pluralKey];
    }
    return getFallbackPluralForm(value);
  }
  return String(value);
}

// src/util/substituteVariables.ts
function hasPlaceholders(text) {
  return /%\{([\w\.]+)\}/g.test(text);
}
function replacePlaceholder(match, varName, variables) {
  return variables[varName] !== void 0 ? String(variables[varName]) : match;
}
function substituteVariables(text, variables) {
  if (!text || !variables || Object.keys(variables).length === 0) {
    return text;
  }
  if (!hasPlaceholders(text)) {
    return text;
  }
  return text.replace(/%\{([\w\.]+)\}/g, (match, varName) => {
    return replacePlaceholder(match, varName, variables);
  });
}

// src/context.ts
function getState() {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error("Function must be called within a setup function or a component");
  }
  const state = inject(VueTranslateSymbol);
  if (!state) {
    throw new Error(
      "Vue Translate plugin is not installed. Make sure to call app.use(vueTranslate) before using it"
    );
  }
  return {
    state,
    instance
  };
}
function createSetLocale(state) {
  return (newLocale) => {
    if (state.availableLocales.includes(newLocale)) {
      state.locale.value = newLocale;
    } else {
      console.warn(
        `Locale "${newLocale}" is not available. Available locales: ${state.availableLocales.join(", ")}`
      );
    }
  };
}
function findTranslationValue(key, component, state) {
  if (!state) {
    const result = getState();
    state = result.state;
    component = result.instance;
  }
  const locale = state.locale.value;
  let localTranslations = {};
  if (component) {
    try {
      if ("type" in component && component.type && "vueTranslate" in component.type) {
        localTranslations = component.type.vueTranslate;
      } else if ("$options" in component && component.$options && "vueTranslate" in component.$options) {
        localTranslations = component.$options.vueTranslate;
      } else if ("vueTranslate" in component) {
        localTranslations = component.vueTranslate;
      }
    } catch (e) {
    }
  }
  const isLocalLookup = key.startsWith(".");
  let translationValue = null;
  if (isLocalLookup && component) {
    const localKey = key.substring(1);
    const localeTranslations = localTranslations[locale];
    if (localeTranslations) {
      const localValue = getObjectPath(localeTranslations, localKey);
      if (localValue !== void 0 && localValue !== null) {
        translationValue = localValue;
      }
    }
  } else {
    if (state.messages[locale]) {
      const globalValue = getObjectPath(state.messages[locale], key);
      if (globalValue !== void 0 && globalValue !== null) {
        translationValue = globalValue;
      }
    }
  }
  if (translationValue === null) {
    if (state.showMissingTranslationWarnings) {
      if (isLocalLookup) {
        console.warn("[vue-translate] Missing local translation for key:", `${locale}${key}`);
      } else {
        console.warn("[vue-translate] Missing global translation for key:", `${locale}.${key}`);
      }
    }
  }
  return translationValue;
}
function translateKey(key, variables, component, state) {
  const translationValue = findTranslationValue(key, component, state);
  if (translationValue === null) {
    return key;
  }
  let result = translationValue;
  if (variables && "count" in variables && typeof variables.count === "number") {
    result = getPluralForm(translationValue, variables.count);
  } else if (typeof translationValue === "object") {
    result = translationValue.other || translationValue.one || translationValue.zero || key;
  }
  return substituteVariables(String(result), variables);
}
function translateKeyAsArray(key, component, state) {
  const translationValue = findTranslationValue(key, component, state);
  if (translationValue === null) {
    return {};
  }
  if (typeof translationValue !== "object") {
    return { value: String(translationValue) };
  }
  return translationValue;
}
function resolveFormatOptions(value, format, locale, localeFormats, showWarnings) {
  var _a, _b;
  const isDate = value instanceof Date;
  const isNumber = typeof value === "number";
  if (typeof format === "string") {
    if (isDate && (localeFormats == null ? void 0 : localeFormats.date)) {
      const dateFormats = localeFormats.date;
      const resolvedOptions = dateFormats[format];
      if (!resolvedOptions && showWarnings) {
        console.warn(`[vue-translate] Date format '${format}' not found for locale '${locale}'`);
      }
      return resolvedOptions;
    }
    if (isNumber && (localeFormats == null ? void 0 : localeFormats.number)) {
      const numberFormats = localeFormats.number;
      const resolvedOptions = numberFormats[format];
      if (!resolvedOptions && showWarnings) {
        console.warn(`[vue-translate] Number format '${format}' not found for locale '${locale}'`);
      }
      return resolvedOptions;
    }
    if (showWarnings) {
      const valueType = isDate ? "Date" : "Number";
      console.warn(
        `[vue-translate] ${valueType} format '${format}' not found for locale '${locale}'`
      );
    }
  }
  if (format && typeof format === "object") {
    return format;
  }
  if (isDate && ((_a = localeFormats == null ? void 0 : localeFormats.date) == null ? void 0 : _a.default)) {
    return localeFormats.date.default;
  }
  if (isNumber && ((_b = localeFormats == null ? void 0 : localeFormats.number) == null ? void 0 : _b.default)) {
    return localeFormats.number.default;
  }
  return void 0;
}
function localizeValue(value, format, state) {
  if (!state) {
    const result = getState();
    state = result.state;
  }
  const locale = state.locale.value;
  const localeFormats = state.formats[locale];
  const showWarnings = state.showMissingTranslationWarnings;
  const options = resolveFormatOptions(value, format, locale, localeFormats, showWarnings);
  if (value instanceof Date) {
    try {
      const result = formatDate(value, locale, options);
      if (result !== null) {
        return result;
      }
    } catch (error) {
      if (showWarnings) {
        console.warn("[vue-translate] Error formatting date:", error);
      }
    }
  } else if (typeof value === "number") {
    try {
      const result = formatNumber(value, locale, options);
      if (result !== null) {
        return result;
      }
    } catch (error) {
      if (showWarnings) {
        console.warn("[vue-translate] Error formatting number:", error);
      }
    }
  }
  return String(value);
}

// src/composable.ts
function useTranslate() {
  const { state, instance } = getState();
  const t = (key, variables) => {
    return translateKey(key, variables, instance, state);
  };
  const ta = (key) => {
    return translateKeyAsArray(key, instance, state);
  };
  const l = (value, format) => {
    return localizeValue(value, format, state);
  };
  const setLocale = createSetLocale(state);
  return {
    t,
    ta,
    l,
    locale: state.locale,
    availableLocales: state.availableLocales,
    setLocale
  };
}

// src/index.ts
var VueTranslateSymbol = Symbol("vue-translate");
function createVueTranslate(options) {
  const localeRef = ref(options.locale);
  const state = {
    locale: localeRef,
    availableLocales: options.availableLocales,
    showMissingTranslationWarnings: options.showMissingTranslationWarnings || false,
    messages: options.messages || {},
    formats: options.formats || { date: {}, number: {} }
  };
  return {
    // Format a key with variables
    translate: (key, variables) => {
      return translateKey(key, variables, null, state);
    },
    // Format a key with variables, returns an array or object
    translateArray: (key) => {
      return translateKeyAsArray(key, null, state);
    },
    // Format a date/number with options
    localize: (value, format) => {
      return localizeValue(value, format, state);
    },
    // Install method
    install(app) {
      app.provide(VueTranslateSymbol, state);
      app.config.globalProperties.$t = function(key, variables) {
        return translateKey(key, variables, this, state);
      };
      app.config.globalProperties.$ta = function(key) {
        return translateKeyAsArray(key, this, state);
      };
      app.config.globalProperties.$l = function(value, format) {
        return localizeValue(value, format, state);
      };
    }
  };
}
export {
  VueTranslateSymbol,
  createVueTranslate,
  useTranslate
};
//# sourceMappingURL=index.js.map